import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Icon from "../AppIcon";
import Button from "./Button";

const TopBar = () => (
  <div className="fixed top-0 left-0 right-0 z-50 bg-primary text-white text-xs py-2 px-4">
    <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
      <div className="flex items-center gap-3 sm:gap-5">
        <a
          href="tel:+9779851157213"
          className="flex items-center gap-1.5 hover:text-white/80 transition-colors"
        >
          <Icon name="Phone" size={12} />
          <span>+977-9851157213</span>
        </a>
        <a
          href="mailto:imsherpatrekker@gmail.com"
          className="flex items-center gap-1.5 hover:text-white/80 transition-colors"
        >
          <Icon name="Mail" size={12} />
          <span>imsherpatrekker@gmail.com</span>
        </a>
      </div>
      <div className="hidden sm:flex items-center gap-1.5 text-white/70">
        <Icon name="MapPin" size={12} />
        <span>Thamel, Kathmandu, Nepal</span>
      </div>
    </div>
  </div>
);

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navItems = [
    { name: "Trekking", path: "/trek-discovery-hub", icon: "Mountain" },
    { name: "Activities", path: "/activities", icon: "Zap" },
    { name: "Our Guides", path: "/sherpa-guide-profiles", icon: "Users" },
    { name: "Stories", path: "/traveler-stories-hub", icon: "BookOpen" },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      <TopBar />
      <header
        className={`fixed left-0 right-0 z-40 transition-all duration-300 ${
          scrolled
            ? "bg-white/98 backdrop-blur-md shadow-sm border-b border-border/80"
            : "bg-white/90 backdrop-blur-sm border-b border-transparent"
        }`}
        style={{ top: "33px" }}
      >      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">

          {/* Logo */}
          <a href="/" className="flex items-center gap-3 group flex-shrink-0">
            {/* Logo mark with brand color ring */}
            <div className="relative">
              <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-xl overflow-hidden ring-2 ring-primary/20 group-hover:ring-primary/50 transition-all duration-300 shadow-sm">
                <img
                  src="/assets/images/logo.jpeg"
                  alt="Sherpa Trekker"
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Small accent dot */}
              <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-sunrise-gold rounded-full border-2 border-white" />
            </div>
            <div>
              <span className="font-headline text-lg lg:text-xl text-foreground leading-none block" style={{ fontWeight: 300 }}>
                Sherpa Trekker
              </span>
              <span className="text-xs text-text-secondary hidden sm:block mt-0.5">
                Where Adventure Meets Authenticity
              </span>
            </div>
          </a>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => (
              <a
                key={item.path}
                href={item.path}
                className={`group relative flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
                  isActive(item.path)
                    ? "text-accent"
                    : "text-gray-600 hover:text-accent"
                }`}
              >
                <Icon name={item.icon} size={16} />
                {item.name}
                {isActive(item.path) && (
                  <span className="absolute -bottom-0.5 left-2 right-2 h-0.5 bg-accent rounded-full" />
                )}
                <span className="absolute -bottom-0.5 left-2 right-2 h-0.5 bg-accent rounded-full scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
              </a>
            ))}
          </nav>

          {/* Desktop CTAs */}
          <div className="hidden lg:flex items-center gap-3">
            <a
              href="tel:+9779851157213"
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 hover:text-accent rounded-xl hover:bg-accent/5 transition-all duration-200"
            >
              <Icon name="Phone" size={15} />
              +977-9851157213
            </a>
            <button
              onClick={() => navigate("/booking-consultation-center")}
              className="flex items-center gap-2 px-5 py-2.5 bg-accent text-white text-sm font-semibold rounded-xl hover:bg-accent/90 transition-all duration-200 shadow-sm hover:shadow-md"
            >
              <Icon name="Calendar" size={15} />
              Book Consultation
            </button>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 rounded-xl text-gray-600 hover:bg-gray-100 transition-colors"
            aria-label="Toggle menu"
          >
            <Icon name={isMobileMenuOpen ? "X" : "Menu"} size={22} />
          </button>
        </div>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden border-t border-border py-4 animate-fade-in">
            <nav className="space-y-1 mb-4">
              {navItems.map((item) => (
                <a
                  key={item.path}
                  href={item.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                    isActive(item.path)
                      ? "text-accent bg-accent/5 font-semibold"
                      : "text-gray-700 hover:text-accent hover:bg-accent/5"
                  }`}
                >
                  <Icon name={item.icon} size={18} />
                  {item.name}
                </a>
              ))}
            </nav>
            <div className="pt-4 border-t border-gray-100 space-y-2">
              <a
                href="tel:+9779851157213"
                className="flex items-center gap-2 px-4 py-3 text-sm text-gray-600 rounded-xl hover:bg-gray-50"
              >
                <Icon name="Phone" size={16} />
                +977-9851157213
              </a>
              <button
                onClick={() => { navigate("/booking-consultation-center"); setIsMobileMenuOpen(false); }}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-accent text-white text-sm font-semibold rounded-xl"
              >
                <Icon name="Calendar" size={16} />
                Book Consultation
              </button>
            </div>
          </div>
        )}
      </div>
    </header>
    </>
  );
};

export default Header;
